import React, { ReactNode, memo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const TriggerWrapper = styled.div`
	display: inline-block;
	position: relative;
`;

const WhisperContent = styled.div<{ placement: string }>`
	position: fixed;
	z-index: 9999;
`;

const CustomWhisper: React.FC<CustomWhisper.Props> = memo(
	({
		children,
		speaker,
		placement = "bottom",
		trigger = "hover",
		...props
	}) => {
		const [visible, setVisible] = useState(false);
		const [popoverPosition, setPopoverPosition] = useState({
			top: 0,
			left: 0,
		});
		const triggerRef = useRef<HTMLDivElement>(null);

		const show = () => setVisible(true);
		const hide = () => setVisible(false);
		const toggle = () => setVisible((prev) => !prev);

		useEffect(() => {
			if (trigger === "click") {
				const handleDocumentClick = (e: MouseEvent) => {
					if (
						triggerRef.current &&
						!triggerRef.current.contains(e.target as Node)
					) {
						setVisible(false);
					}
				};
				document.addEventListener("click", handleDocumentClick);
				return () => {
					document.removeEventListener("click", handleDocumentClick);
				};
			}
			return undefined;
		}, [trigger]);

		useEffect(() => {
			if (visible && triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();
				const offset = 0;
				const offset2 = 47;
				let top = 0;
				let left = 0;
				if (placement === "top") {
					top = rect.top - offset;
					left = rect.left + rect.width;
				} else if (placement === "bottom") {
					top = rect.bottom + offset;
					left = rect.left + rect.width - offset2;
				} else if (placement === "left") {
					top = rect.top + rect.height;
					left = rect.left - offset;
				} else if (placement === "right") {
					top = rect.top + rect.height;
					left = rect.right + offset;
				}
				setPopoverPosition({ top, left });
			}
		}, [visible, placement]);

		const eventHandlers: { [key: string]: any } = {};
		if (trigger === "hover") {
			eventHandlers.onMouseEnter = show;
			eventHandlers.onMouseLeave = hide;
		} else if (trigger === "click") {
			eventHandlers.onClick = toggle;
		} else if (trigger === "focus") {
			eventHandlers.onFocus = show;
			eventHandlers.onBlur = hide;
		}

		return (
			<TriggerWrapper ref={triggerRef} {...eventHandlers} {...props}>
				{children}
				{visible &&
					speaker &&
					createPortal(
						<WhisperContent
							placement={placement}
							style={{
								top: popoverPosition.top,
								left: popoverPosition.left,
							}}
						>
							{speaker}
						</WhisperContent>,
						document.body,
					)}
			</TriggerWrapper>
		);
	},
);

declare namespace CustomWhisper {
	interface Props {
		children: ReactNode;
		speaker?: ReactNode;
		placement?: "top" | "bottom" | "left" | "right";
		trigger?: "hover" | "click" | "focus";
	}
}

export default CustomWhisper;
